import { useCallback, useMemo } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';

import { RdProject } from '@neo.tax/rd/hooks';
import { ProjectPurpose, ProjectPurposeImprovedAspect } from '@neo.tax/shared';

import { getRdProjectPurposeImprovedAspectsSet, RD_PROJECT_PURPOSE_IMPROVED_ASPECTS } from '../utils';

interface UseRdProjectPurposeFormProps {
  rdProject: RdProject;
}

export type RdProjectPurposeForm = {
  purpose: ProjectPurpose | null;
  improvedAspects: {
    [key in ProjectPurposeImprovedAspect]: boolean;
  };
};

export const useRdProjectPurposeForm = ({
  rdProject,
}: UseRdProjectPurposeFormProps): UseFormReturn<RdProjectPurposeForm> => {
  const projectImprovedAspectSet = useMemo(
    () => getRdProjectPurposeImprovedAspectsSet(rdProject.improvedAspects),
    [rdProject.improvedAspects],
  );

  const { reset, ...form } = useForm<RdProjectPurposeForm>({
    mode: 'onSubmit',
    defaultValues: {
      purpose: rdProject.purpose,
      improvedAspects: RD_PROJECT_PURPOSE_IMPROVED_ASPECTS.map((improvedAspect) => ({
        [improvedAspect]: projectImprovedAspectSet.has(improvedAspect),
      })).reduce((acc, curr) => ({ ...acc, ...curr }), {}),
    },
  });

  const resetForm = useCallback(
    (): void =>
      reset({
        purpose: rdProject.purpose,
        improvedAspects: RD_PROJECT_PURPOSE_IMPROVED_ASPECTS.map((improvedAspect) => ({
          [improvedAspect]: projectImprovedAspectSet.has(improvedAspect),
        })).reduce((acc, curr) => ({ ...acc, ...curr }), {}),
      }),
    [rdProject.purpose, projectImprovedAspectSet, reset],
  );

  return {
    ...form,
    reset: resetForm,
  };
};
